import { BaseScreen } from "../../../screens/BaseScreen";
import { Button } from "../../../ui/Button";
import { LAYOUT, TEXT_STYLES, COLORS } from "../../../constants";
import { MainMenuScene } from "../../MainMenuScene";
import type { ScreenContext } from "../../../screens/types";
import { tagTextForAutomation } from "../../../automation/automationTags";
import { getSelectedCharacter } from "../../../state/characterPersistence";
import { CharacterFactory } from "../../../characters";
import { PrimitiveAvatar } from "../../../avatars/PrimitiveAvatar";
import { PLAYER_BLUEPRINT } from "../../../avatars/blueprints/player";
import { BARBARIAN_BLUEPRINT } from "../../../avatars/blueprints/barbarian";
import { newRun } from "../../../state/runState";
import { newOverworldState } from "../../../state/overworldState";
import { OVERWORLD_SCENE_KEY } from "../../OverworldScene";

/**
 * StartScreen - Main menu with character preview and quick-start
 */
export class StartScreen extends BaseScreen<MainMenuScene> {
  constructor(scene: MainMenuScene) {
    super(scene, "START");
  }

  enter(_context?: ScreenContext) {
    const { width, height } = this.scene.scale;

    // Title
    const title = this.scene.add
      .text(width / 2, LAYOUT.screenPadding + 20, "goobijex", TEXT_STYLES.title)
      .setOrigin(0.5, 0);
    this.addToContainer(title);
    tagTextForAutomation(title, "start-title");

    // Get selected character
    const selectedCharacterId = getSelectedCharacter();
    const character = CharacterFactory.get(selectedCharacterId);

    // Character preview section
    const previewY = height * 0.35;

    // Avatar
    let avatarBlueprint;
    if (character.sprite === "player") {
      avatarBlueprint = PLAYER_BLUEPRINT;
    } else if (character.sprite === "barbarian") {
      avatarBlueprint = BARBARIAN_BLUEPRINT;
    }

    if (avatarBlueprint) {
      const avatarSize = 80;
      const avatarScale = avatarSize / avatarBlueprint.designSize.w;
      const avatar = new PrimitiveAvatar(this.scene, avatarBlueprint, { scale: avatarScale });
      avatar.setPosition(width / 2, previewY);
      this.addToContainer(avatar.getRoot());
    }

    // Character name
    const characterName = this.scene.add
      .text(width / 2, previewY + 60, character.name, {
        ...TEXT_STYLES.subtitle,
        fontSize: "28px",
        color: COLORS.textPrimary,
      })
      .setOrigin(0.5, 0);
    this.addToContainer(characterName);
    tagTextForAutomation(characterName, "selected-character-name");

    // Choose Character button - positioned below character name
    const buttonWidth = width - LAYOUT.screenPadding * 2;
    const chooseButtonY = previewY + 110;
    const chooseButton = new Button(this.scene, LAYOUT.screenPadding, chooseButtonY, {
      width: buttonWidth,
      height: LAYOUT.buttonHeight * 0.7,
      label: "Choose Character",
      fillColor: COLORS.buttonFill,
      strokeColor: COLORS.buttonStroke,
      automationName: "choose-character",
      onClick: () => {
        this.scene.showCharacterSelect();
      },
    });
    this.addToContainer(chooseButton);

    // Start Run button (primary) - at bottom
    const startButtonY = height - LAYOUT.screenPadding - LAYOUT.buttonHeight;
    const startButton = new Button(this.scene, LAYOUT.screenPadding, startButtonY, {
      width: buttonWidth,
      height: LAYOUT.buttonHeight,
      label: "Start Run",
      fillColor: COLORS.skillCardFill,
      strokeColor: COLORS.skillCardStroke,
      automationName: "start-run",
      onClick: () => {
        // Create run with selected character and start game
        const run = newRun(selectedCharacterId);
        const overworldState = newOverworldState(run);
        this.scene.scene.start(OVERWORLD_SCENE_KEY, { overworldState });
      },
    });
    this.addToContainer(startButton);
  }
}
